import { useMemo } from 'react';
import type { FileRef, FileRefError, FileRefType } from '@/types/file';
import { formMessages } from '@/constants';
import { getLocalEcho } from './localEcho';

/**
 * The four states any file-bearing surface has to distinguish (file-handling
 * §14.1). `absent` is the only meaning of a null FileRef; `processing` means
 * the bytes exist server-side but no rendition does yet; `failed` always
 * carries a reason.
 */
export type FileRefState = 'absent' | 'processing' | 'ready' | 'failed';

export interface FileRefView {
  state: FileRefState;
  /** What to render right now: the ready rendition, or the local echo. */
  url: string | null;
  /** The thumbnail rendition, when the surface's FileRef carries one and is ready. */
  thumbnailUrl: string | null;
  filename: string | null;
  size: number | null;
  error: FileRefError | null;
  /** True when `url` is a local object URL rather than a server rendition. */
  isLocalEcho: boolean;
  /** `FileRef.type`, or null when absent — drives expected-duration copy. */
  type: FileRefType | null;
  /**
   * Attachment-disposition URL, when the surface's FileRef carries one. Set
   * for ready files, and for the one FAILED case where the bytes are still
   * published (worker moved them, then couldn't process — file-handling plan
   * A3 row 10): the reviewer can still get the original.
   */
  downloadUrl: string | null;
}

export interface UseFileRefOptions {
  /**
   * The S3 upload key this surface just uploaded, if any. Used to look up the
   * local echo registered by `uploadFileToS3`. Null/undefined disables echo.
   */
  localKey?: string | null;
}

/**
 * Type-aware "how long will this take" copy (§14.1: duration derives from
 * `type`, not from a pipeline stage). Images and documents finish in seconds;
 * video and audio go through MediaConvert and take minutes.
 *
 * Exported for surfaces that write their own processing copy instead of
 * rendering `<FileAttachment>`.
 */
export function fileRefProcessingCopy(type: FileRefType | null | undefined): string {
  return type === 'video' || type === 'audio' ? 'a few minutes' : 'a few seconds';
}

/**
 * Manager-facing copy for a failed file: the reason when the code has one
 * (malware, upload never finished, media transcode), the blameless generic
 * otherwise. Never used on learner surfaces (plan A1.3).
 */
export function fileRefFailureCopy(error: FileRefError | null | undefined): string {
  return (error && formMessages.upload.failedReason[error]) || formMessages.upload.failed;
}

const ABSENT: FileRefView = {
  state: 'absent',
  url: null,
  thumbnailUrl: null,
  filename: null,
  size: null,
  error: null,
  isLocalEcho: false,
  type: null,
  downloadUrl: null,
};

/**
 * Collapse a `FileRef` (plus the uploader's local bytes, when we have them)
 * into one view a surface can render without re-deriving precedence.
 *
 * Precedence (§14.3.6): ready server URL → local echo → honest processing
 * state → absent. The echo never overrides a ready rendition, and it never
 * upgrades `state`: a file that is still processing reports `processing` even
 * while showing echo bytes. Whether that reads as "done" to the user is the
 * surface's call — an <img> can simply render it, a chip may still say
 * "(processing)".
 */
export function useFileRef(
  fileRef: FileRef | null | undefined,
  opts: UseFileRefOptions = {},
): FileRefView {
  const localKey = opts.localKey ?? null;

  // `fileRef` is a plain wire object; identity changes with each refetch,
  // which is exactly when this should recompute.
  return useMemo(() => resolveFileRef(fileRef, localKey), [fileRef, localKey]);
}

/**
 * The hook's body as a plain function, for surfaces that resolve a FileRef
 * inside a render callback (a list row, a `map`) where a hook cannot be
 * called. Same precedence, same result — the hook is this plus memoization.
 */
export function resolveFileRef(
  fileRef: FileRef | null | undefined,
  localKey: string | null | undefined = null,
): FileRefView {
  {
    const echo = getLocalEcho(localKey);

    if (!fileRef) {
      if (!echo) return ABSENT;
      // Uploaded in this session but the parent resource hasn't echoed a
      // FileRef back yet — the bytes are real, so show them and say so.
      return {
        state: 'processing',
        url: echo.objectURL,
        thumbnailUrl: null,
        filename: echo.filename,
        size: echo.size,
        error: null,
        isLocalEcho: true,
        type: null,
        downloadUrl: null,
      };
    }

    const filename = fileRef.filename ?? echo?.filename ?? null;
    const size = fileRef.size ?? echo?.size ?? null;

    if (fileRef.status === 'ready') {
      return {
        state: 'ready',
        url: fileRef.url ?? null,
        thumbnailUrl: fileRef.thumbnailUrl ?? null,
        filename,
        size,
        error: null,
        isLocalEcho: false,
        type: fileRef.type,
        downloadUrl: fileRef.downloadUrl ?? null,
      };
    }

    if (fileRef.status === 'failed') {
      // Never RENDER bytes we know the server rejected (a virus-flagged file
      // most of all) — failed is failed, with a reason. The server offers a
      // download only when the bytes are published and harmless (A3 row 10);
      // a malware/upload-missing FileRef never carries one.
      return {
        state: 'failed',
        url: null,
        thumbnailUrl: null,
        filename,
        size,
        error: fileRef.error ?? null,
        isLocalEcho: false,
        type: fileRef.type,
        downloadUrl: fileRef.downloadUrl ?? null,
      };
    }

    return {
      state: 'processing',
      url: echo?.objectURL ?? null,
      thumbnailUrl: null,
      filename,
      size,
      error: null,
      isLocalEcho: Boolean(echo),
      type: fileRef.type,
      downloadUrl: null,
    };
  }
}
